/// <reference types='cordova-plugin-file-transfer' />
import { Result, Media } from '@/classes'
import { fetchAPI } from '@/utils/useAPI'
import { useStore } from '@/utils/useStore'
import { mediaUploadAPI, mediaUploadCompleteAPI, mediaUploadRequestAPI } from '@/api/media'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import ToastsStore from '@/store/toasts'
import { getRandomString } from '~/utils/useRandom'

const store = defineStore('medias', () => {
  const toastsStore = ToastsStore()
  const { getItem, getItems, setItem, deleteItem, } = useStore('media')

  function formatToMimeType(format: string) {
    const mimeTypes = {
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      bmp: 'image/bmp',
      tiff: 'image/tiff',
      ico: 'image/x-icon',
      pdf: 'application/pdf',
      txt: 'text/plain',
      html: 'text/html',
      css: 'text/css',
      js: 'application/javascript',
      json: 'application/json',
      xml: 'application/xml',
      mp4: 'video/mp4',
      m4a: 'audio/aac',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
    }

    return mimeTypes[format.toLowerCase() as keyof typeof mimeTypes] ?? ''
  }

  function getFileReader(): FileReader {
    const fileReader = new FileReader();
    const zoneOriginalInstance = (fileReader as any)["__zone_symbol__originalInstance"];
    return zoneOriginalInstance || fileReader;
  }

  const createFile = async (
    params: {
      uri: string,
      fileType: string
      directory?: string
      isPalette?: boolean
      size?: 'default' | 'events' | 'post' | 'profile-picture' | 'story'
      type?: 'image' | 'gif' | 'audio'
      extract?: {
        x: number
        y: number
        width: number
        height: number
        rotate: number
        scaleX: number
        scaleY: number
      }
    }
  ) => {
    const info = {
      step: '',
      mimetype: '',
      directory: '',
      url: '',
      token: '',
      extract: {}
    }

    const timeout = setTimeout(() => {
      console.log(JSON.stringify(info))
    
      useNuxtApp().$amplitude.logEvent(`Error`, {
        message: 'possible-timeout',
        info: JSON.stringify(info),
      })
    }, 50000)
    
    try {
      info.step = 'requestStart'
      info.mimetype = formatToMimeType(params.fileType)
      info.directory = params.directory || 'default'
      info.extract = params.extract || {}

      useNuxtApp().$amplitude.logEvent(`Image_start`)

      const response = await mediaUploadRequestAPI({
        directory: params.directory,
        type: params.type,
        mimetype: formatToMimeType(params.fileType),
        size: params.size,
        isPalette: params.isPalette,
        ...(params.extract ? { extract: params.extract } : {})
      })

      info.step = 'requestReceived'

      if (!response.ok) throw response

      info.step = 'uploadStart'
      info.url = response.data.url

      let responseUpload = { ok: false }

      if (Capacitor.isNativePlatform()) {
        try {        
          info.step = 'fileTransferStart'
          
          const transfer = new FileTransfer()
          responseUpload = await new Promise((resolve, reject) => {
            transfer.upload(params.uri, response.data.url, () => {
              resolve({ ok: true })
            }, (error) => {
              reject(error)
            }, {
              httpMethod: 'PUT',
              chunkedMode: false,
              headers: {
                'Content-Type': formatToMimeType(params.fileType)
              }
            })
          })

        } catch (e) {
          console.error(e)
        }
      } else {
        const res = await fetch(params.uri);
        const blob = await res.blob()
        const file = new File([ blob ], getRandomString() + '.' + params.fileType, { type: blob.type })

        responseUpload = await mediaUploadAPI({
          file: file,
          url: response.data.url
        })
      }
            
      if (!responseUpload.ok) throw responseUpload

      info.step = 'uploadCompleteStart'
      info.token = response.data.token

      const responseComplete = await mediaUploadCompleteAPI(response.data.token)
      if (!responseComplete.ok) throw responseComplete
      
      info.step = 'uploadCompleted'

      const createdItem = responseComplete.data.media
      setItem(createdItem)

      clearTimeout(timeout)

      return {
        ok: true,
        data: new Media(createdItem)
      }
    } catch (e: any) {
      console.error(e)
      console.log(JSON.stringify(info))
      
      useNuxtApp().$amplitude.logEvent(`Error`, {
        message: e.message,
        stack: e.stack,
        info: JSON.stringify(info),
        name: e.name,
        ...e
      })

      clearTimeout(timeout)

      return { ok: false }
    }
  }

  const deleteFile = async (mediaId: string) => {
    const response: Result<{ success: boolean }> = await fetchAPI('media', { _id: mediaId }, { method: 'DELETE' })

    if (!response.ok || !response.data?.success) {
      toastsStore.error(response)
      return { ok: false }
    }

    deleteItem(mediaId)

    return {
      ok: true,
      data: {
        success: true
      }
    }
  }

  return {
    getItem, getItems, setItem, deleteItem, deleteFile, createFile
  }
})

export default store
